/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2025 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { editLocalization, getLocalization } from '~/api/common/localizationMode/localization';
import { getLocalizationIgnoreCache as updateLocalization } from '~common-store/slices/localization';
import Modal from '~components/modal/Modal';

import { displayToastForAPIResponse, useAddToast } from '../alerts/ToastContext';
import { getAutomationID, getExtraAutomationID } from '../id/AutomationID';
import IDList from '../id/IDList';
import MappingID from '../id/MappingID';
import { buildOrderingID } from '../id/RootAutomationID';
import Checkbox from '../inputs/Checkbox';
import TextInput from '../inputs/TextInput';
import ConfirmModalButton from '../modal/ConfirmModalButton';

import LocalizedString from './LocalizedString';

const LocalizationEditModal = ({ toggleModal, localeKey }) => {
    const rootID = buildOrderingID('editLocalization');
    const automationID = getAutomationID(rootID, 'modal');

    const addToast = useAddToast();
    const dispatch = useDispatch();

    const [description, setDescription] = useState('');
    const [locales, setLocales] = useState([]);

    useEffect(() => {
        (async () => {
            // On load, retrieve the information for this particular locale key
            const localizationResponse = await getLocalization(localeKey);
            setDescription(localizationResponse.description);
            setLocales(localizationResponse.locales);
        })();
    }, [localeKey]);


    // Update a particular locale of the locales array
    // This is basically just "staging" information and not saving anything yet
    const setValue = (localeID, newValue, isResetToDefault) => {
        setLocales((prevState) => {
            const index = prevState.findIndex((c) => c.id === localeID);
            if (index !== -1) {
                const newValues = [...prevState];

                const newObject = {
                    ...newValues[index],
                };

                if (newValue !== undefined) {
                    newObject.value = newValue;
                }

                if (isResetToDefault !== undefined) {
                    newObject.isResetToDefault = isResetToDefault;
                }

                newValues[index] = newObject;
                return newValues;
            } else {
                return prevState;
            }
        });
    };

    // Actually send the locales array to the server to save everything
    const saveAction = async () => {
        const localizationResponse = await editLocalization(localeKey, locales);
        displayToastForAPIResponse(addToast, localizationResponse, 'edit localization');

        if (localizationResponse.success) {
            // Update the localization on UI in real-time
            dispatch(updateLocalization(localeKey));
        }
    };

    const localeIDs = [];

    // We don't want edit localization buttons inside this modal itself or we have a loop of forever opening localization modals
    // So we'll pass "allowLocalizationMode" as false to disable edit buttons for this modal only
    return (
        <Modal
            isOpen
            toggle={toggleModal}
            closeOnBGClick
            title={<LocalizedString localeKey="COMMON_UI.TITLE.EDIT_LOCALIZATION" allowLocalizationMode={false} />}
            size="lg"
            footer={<ConfirmModalButton
                id={getExtraAutomationID(automationID, 'updateButton').id}
                toggleModal={toggleModal}
                label={<LocalizedString localeKey="COMMON_UI.BUTTON.LOCALIZATION_EDIT_MODAL__SAVE" allowLocalizationMode={false} />}
                action={saveAction}
            />}
        >
            <div id={getExtraAutomationID(automationID, 'details').id} className="localization-details">
                <LocalizedString
                    localeKey="COMMON_UI.LABEL.LOCALIZATION_EDIT_MODAL__LOCALIZATION_DETAILS"
                    parameters={{
                        LOCALE_KEY: localeKey,
                        DESCRIPTION: description,
                    }}
                    allowLocalizationMode={false}
                />
            </div>

            <div className="localization-instructions">
                <LocalizedString localeKey="ROLE.LABEL.CHANGELOCALIZATION" allowLocalizationMode={false}/>
            </div>

            {locales.map((locale) => {
                const updateValue = (newValue) => setValue(locale.id, newValue, undefined);
                const updateSetDefault = (newValue) => setValue(locale.id, undefined, newValue);

                localeIDs.push(locale.id);

                return (
                    <div key={locale.id} className="localization-edit-section">
                        <TextInput
                            automationID={getExtraAutomationID(automationID, 'valueInput', locale.id)}
                            type="text"
                            label={locale.name}
                            value={locale.value}
                            setValue={updateValue}
                        />

                        <Checkbox
                            automationID={getExtraAutomationID(automationID, 'resetToDefaultCheckbox', locale.id)}
                            label={{ key: 'COMMON_UI.LABEL.RESET_TO_DEFAULT' }}
                            checked={locale.isResetToDefault}
                            setChecked={updateSetDefault}
                            allowLocalizationMode={false}
                        />

                        <MappingID id={locale.id} name={locale.name} label="locale"/>
                    </div>
                );
            })}
            <IDList ids={localeIDs} label="localeIDs"/>
        </Modal>
    );
};

LocalizationEditModal.propTypes = {
    toggleModal: PropTypes.func,
    localeKey: PropTypes.string,
};

export default LocalizationEditModal;